import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { getDefaultTheme } from "../constants/themeDefaults";

import ClassiqueHero from "../templates/ClassiqueHero";
import ModerneHero from "../templates/ModerneHero";
import ElegantHero from "../templates/ElegantHero";
import TempleHero from "../templates/TempleHero";

interface InvitationData {
  invite: {
    nom: string;
    table?: string;
    couple?: string;
    statut?: string;
  };
  mariage: {
    template?: string;
    jourSemaine?: string;
    dateISO: string;
    date?: string;
    heure?: string;
    bride?: string;
    groom?: string;
    photoBackground?: string;
    invitationImage?: string;
    boissonsSansAlcool?: string[];
    boissonsAvecAlcool?: string[];
    numeroMarie?: string;
    numeroPicApic?: string;
    mapEmbedUrl?: string;
    theme?: any;
    livreOrActive?: boolean;
    [key: string]: any;
  };
  error?: string;
}

interface InvitationResponse {
  success: boolean;
  mariage: any;
  invite: {
    nom: string;
    table?: string;
    couple?: string;
    statut?: string;
  };
  error?: string;
}

const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<InvitationData | null>(null);

  const updateOpenGraphTags = (mariage: any, invite: any) => {
    const imageUrl = mariage.photoBackground 
      ? `https://mwaliko.click/uploads/${mariage.photoBackground}`
      : `https://mwaliko.click/logP.png`;
    
    const title = `Invitation au mariage de ${mariage.bride} & ${mariage.groom}`;
    const description = `${invite.nom}, vous êtes invité(e) au mariage de ${mariage.bride} & ${mariage.groom} le ${mariage.date}`;
    
    // Mettre à jour og:image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', imageUrl);
    
    // Mettre à jour og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title);
    
    // Mettre à jour og:description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', description);
    
    // Mettre à jour twitter:image
    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    twitterImage.setAttribute('content', imageUrl);
    
    // Mettre à jour twitter:title
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', title);
  };

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await api.get<InvitationResponse>(`/mariages/invitation/${token}`);
        const json = response.data;
        
        console.log("Données reçues du backend:", json);
        
        const invitationData = {
          invite: {
            nom: json.invite?.nom || "",
            table: json.invite?.table || "",
            couple: json.invite?.couple || "non",
            statut: json.invite?.statut || "pending"
          },
          mariage: json.mariage || {}
        };
        
        setData(invitationData);
        
        // Mettre à jour les balises Open Graph
        if (json.mariage && json.invite) {
          updateOpenGraphTags(json.mariage, json.invite);
        }
      } catch (error) {
        console.error("Erreur chargement invitation:", error);
        setData({ 
          error: "Invitation introuvable", 
          invite: { nom: "" }, 
          mariage: { dateISO: "" } 
        });
      }
    };
    fetchInvitation();
  }, [token]);

  if (!data) return <p>Chargement...</p>;
  if (data.error) return <p>Invitation introuvable</p>;

  const { mariage, invite } = data;

  const getJourSemaine = (dateISO: string) => {
    if (!dateISO) return "";
    const date = new Date(dateISO);
    const jours = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    return jours[date.getDay()];
  };

  const defaultTheme = getDefaultTheme(mariage.template || "ClassiqueHero");
  const mergedTheme = {
    ...defaultTheme,
    ...(mariage.theme || {})
  };

  const commonProps = {
    photoBackground: mariage.photoBackground || "",
    inviteNom: invite.nom,
    jourSemaine: mariage.jourSemaine || getJourSemaine(mariage.dateISO),
    date: mariage.date || "",
    heure: mariage.heure || "",
    dateISO: mariage.dateISO,
    heureISO: mariage.heureISO || "",
    invitationImage: mariage.invitationImage || "",
    tableName: invite.table || "",
    boissonsSansAlcool: mariage.boissonsSansAlcool || [],
    boissonsAvecAlcool: mariage.boissonsAvecAlcool || [],
    numeroMarie: mariage.numeroMarie || "",
    numeroPicApic: mariage.numeroPicApic || "",
    mapEmbedUrl: mariage.mapEmbedUrl || "",
    bride: mariage.bride || "",
    groom: mariage.groom || "",
    theme: mergedTheme,
    couple: invite.couple || "non",
    inviteToken: token,
    mariageId: mariage._id || "",
    livreOrActive: mariage.livreOrActive ?? true   
  };

  switch (mariage.template) {
    case "ClassiqueHero":
    case "BeigeMarron":
    case "CarminRose":
      return <ClassiqueHero 
        key={`classique-${token}-${mariage.template}`} 
        {...commonProps} 
      />;
    
    case "ModerneHero":
    case "VertForet":
    case "BleuNuit":
      return <ModerneHero 
        key={`moderne-${token}-${mariage.template}`} 
        {...commonProps} 
      />;
    
    case "ElegantHero":
    case "MauveSaphir":
    case "BordeauxPeche":
    case "RoseBordeaux":
      return <ElegantHero 
        key={`elegant-${token}-${mariage.template}`} 
        {...commonProps} 
      />;
    
    case "TempleHero":
    case "TempleNoir":
    case "TempleSable":
      return <TempleHero 
        key={`temple-${token}-${mariage.template}`} 
        {...commonProps} 
      />;
    
    default:
      return <p>Template inconnu</p>;
  }
};

export default InvitationPage;